import React, { useMemo } from "react";
import { HorizontalBar, Bar, Doughnut } from "react-chartjs-2";
import { Card, CardBody, CardTitle, Row, Col } from "reactstrap";
import { buildMarkerWorkloadRows } from "utils/markerWorkloadRows";

const COLORS = {
  marked: "rgba(45, 206, 137, 0.85)",
  pending: "rgba(251, 99, 64, 0.85)",
  assigned: "rgba(94, 114, 228, 0.85)"
};

const DOUGHNUT_COLORS = [
  "#5e72e4",
  "#2dce89",
  "#fb6340",
  "#11cdef",
  "#f5365c",
  "#172b4d",
  "#8965e0",
  "#ffd600"
];

/**
 * Charts for marker workload distribution (reporting year from funnel).
 */
export default function AdminMarkerWorkloadCharts({
  funnel,
  funnelLoading,
  statsYear,
  statsLoading
}) {
  const yearLabel =
    funnel && funnel.year != null ? funnel.year : statsYear;

  const rows = useMemo(() => buildMarkerWorkloadRows(funnel), [funnel]);

  const topByPending = useMemo(() => {
    return [...rows].sort((a, b) => b.pending - a.pending).slice(0, 12);
  }, [rows]);

  const stackedHorizontalData = useMemo(() => {
    const labels = topByPending.map(r => r.chartLabel);
    return {
      labels,
      datasets: [
        {
          label: "Marked",
          data: topByPending.map(r => r.marked),
          backgroundColor: COLORS.marked,
          stack: "w"
        },
        {
          label: "Pending",
          data: topByPending.map(r => r.pending),
          backgroundColor: COLORS.pending,
          stack: "w"
        }
      ]
    };
  }, [topByPending]);

  const stackedOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: "bottom" },
      scales: {
        xAxes: [
          {
            stacked: true,
            ticks: { beginAtZero: true }
          }
        ],
        yAxes: [
          {
            stacked: true,
            ticks: { fontSize: 10 }
          }
        ]
      }
    }),
    []
  );

  const assignedBarData = useMemo(() => {
    const top = [...rows].sort((a, b) => b.assigned - a.assigned).slice(0, 10);
    return {
      labels: top.map(r => r.chartLabel),
      datasets: [
        {
          label: "Assigned (total)",
          data: top.map(r => r.assigned),
          backgroundColor: COLORS.assigned
        }
      ]
    };
  }, [rows]);

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      scales: {
        yAxes: [{ ticks: { beginAtZero: true } }],
        xAxes: [{ ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } }]
      }
    }),
    []
  );

  const doughnutData = useMemo(() => {
    const sorted = [...rows].sort((a, b) => b.pending - a.pending);
    const maxSlices = 8;
    const head = sorted.slice(0, maxSlices);
    const tail = sorted.slice(maxSlices);
    const otherPending = tail.reduce((s, r) => s + r.pending, 0);
    const labels = head.map(r => r.chartLabel);
    const data = head.map(r => r.pending);
    if (otherPending > 0) {
      labels.push("Other markers");
      data.push(otherPending);
    }
    const bg = labels.map(
      (_, i) => DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]
    );
    return { labels, datasets: [{ data, backgroundColor: bg }] };
  }, [rows]);

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      legend: { position: "right" }
    }),
    []
  );

  const totalPending = useMemo(
    () => rows.reduce((s, r) => s + r.pending, 0),
    [rows]
  );

  const showLoading = funnelLoading && !funnel;
  const empty =
    !showLoading &&
    funnel &&
    Array.isArray(funnel.by_marker) &&
    funnel.by_marker.length === 0;
  const noFunnelYet = !showLoading && !funnel;

  if (statsLoading.assessed || statsLoading.available) return null;

  if (showLoading || empty || noFunnelYet || rows.length === 0) return null;

  return (
    <Row className="mt-2">
      <Col xs="12">
        <Card className="card-stats mb-4 mb-xl-0 shadow-sm">
          <CardBody>
            <CardTitle tag="h5" className="text-uppercase text-muted mb-1">
              Marker workload charts
            </CardTitle>
            <p className="small text-muted mb-4">
              Distribution for reporting year <strong>{yearLabel}</strong> (same
              data as the Markers table).
            </p>
            <Row>
              <Col lg="6" className="mb-4">
                <h6 className="text-muted mb-3">
                  Marked vs pending (top 12 by pending)
                </h6>
                <div style={{ height: 320 }}>
                  <HorizontalBar data={stackedHorizontalData} options={stackedOptions} />
                </div>
              </Col>
              <Col lg="6" className="mb-4">
                <h6 className="text-muted mb-3">
                  Share of pending workload (top markers)
                </h6>
                <div style={{ height: 320 }}>
                  {totalPending > 0 ? (
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  ) : (
                    <p className="small text-muted pt-4">
                      No pending items to distribute (all workload is marked).
                    </p>
                  )}
                </div>
              </Col>
              <Col xs="12">
                <h6 className="text-muted mb-3">
                  Total assigned by marker (top 10)
                </h6>
                <div style={{ height: 280 }}>
                  <Bar data={assignedBarData} options={barOptions} />
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}
